import { notify } from 'notifications';
import { GetStateFunction } from 'redux/store';
import { FileShortInfoType } from 'services/production-workflow.model';
import { ProductionWorkflowService } from 'services/production-workflow.service';
import { StateController } from 'state-controller';

type ProductionInfo = {
  id: string;
  name: string;
  configurationName: string;
};

export type ProductionImage = {
  isMain: boolean;
  type: 'production' | 'product';
} & FileShortInfoType;

export type ProductionImageModalState = {
  isOpen: boolean;
  isLoading: boolean;
  selectedImageId: string;
  images: ProductionImage[];
  isContainFitMode: boolean;
  productionInfo: ProductionInfo;
};

const defaultState: ProductionImageModalState = {
  images: [],
  isOpen: false,
  isLoading: false,
  isContainFitMode: false,
  productionInfo: {
    id: '',
    name: '',
    configurationName: '',
  },
  selectedImageId: '',
};

const stateController = new StateController<ProductionImageModalState>('PRODUCTION_IMAGE_MODAL', defaultState);

export class ProductionImageModalActions {
  public static openModal(productionInfo: ProductionInfo) {
    return async (dispatch) => {
      dispatch(stateController.setState({ isOpen: true, isLoading: true, productionInfo }));

      try {
        const images = await ProductionImageModalActions.loadProductionImages(productionInfo.id);

        const defaultSelectedId = images.find((img) => img.isMain)?.id || images[0]?.id || '';

        dispatch(
          stateController.setState({
            images,
            isLoading: false,
            selectedImageId: defaultSelectedId,
          }),
        );
      } catch (error) {
        dispatch(
          stateController.setState({
            images: [],
            isLoading: false,
          }),
        );
      }
    };
  }

  public static uploadImages(files: File[], callback?: (imageUrl: string, productionId?: string) => void) {
    return async (dispatch, getState: GetStateFunction) => {
      const { productionInfo, images } = getState().production.productionImageModal;

      try {
        const isFirstUpload = images.length === 0;

        const uploadPromises = files.map(async (file, index) => {
          const formData = new FormData();
          formData.append('file', file, file.name);

          const image = await ProductionWorkflowService.uploadProductionWorkflowImage(productionInfo.id, formData);

          return {
            ...image,
            name: image.name,
            type: 'production' as const,
            isMain: isFirstUpload && index === 0,
            isManuallyUploaded: true,
          };
        });

        const newImages = await Promise.all(uploadPromises);

        const filterPhotos = (type: 'production' | 'product') => {
          return images.filter((img) => img.type === type);
        };

        const productionImages = filterPhotos('production');
        const productImages = filterPhotos('product');

        const sortedImages = [...newImages, ...productionImages, ...productImages];
        const lastNewImage = newImages[newImages.length - 1];

        dispatch(
          stateController.setState({
            images: sortedImages,
            selectedImageId: lastNewImage?.id || '',
          }),
        );

        if (isFirstUpload) {
          dispatch(ProductionImageModalActions.applyImage(lastNewImage));
          callback?.(lastNewImage.thumbnail_medium_link || '', productionInfo.id);
        }
      } catch (error) {
        notify.error(error.message);
      }
    };
  }

  public static deleteImage(imageId: string) {
    return async (dispatch, getState: GetStateFunction) => {
      const { images, selectedImageId } = getState().production.productionImageModal;

      try {
        const deletedImage = images.find((img) => img.id === imageId);
        const remainingImages = images.filter((img) => img.id !== imageId);

        let newSelectedId = selectedImageId;

        if (deletedImage?.isMain && remainingImages.length) {
          const deletedIndex = images.findIndex((img) => img.id === imageId);
          const nextMainIndex = Math.min(deletedIndex, remainingImages.length - 1);
          const nextMainImage = remainingImages[nextMainIndex];

          newSelectedId = nextMainImage.id;
        }

        if (imageId === selectedImageId) {
          newSelectedId = remainingImages.find((img) => img.isMain)?.id || remainingImages[0]?.id || '';
        }

        dispatch(
          stateController.setState({
            images: remainingImages,
            selectedImageId: newSelectedId,
          }),
        );
      } catch (error) {
        notify.error(error.message);
      }
    };
  }

  public static applyImage(
    imageToApply: ProductionImage | null,
    callback?: (imageUrl: string | null, productionId?: string) => void,
  ) {
    return async (dispatch, getState: GetStateFunction) => {
      try {
        const { images, productionInfo } = getState().production.productionImageModal;

        await ProductionWorkflowService.updateProductionWorkflowImage(productionInfo.id, {
          image_url: imageToApply?.thumbnail_medium_link || null,
        });

        callback?.(imageToApply?.thumbnail_medium_link || null, productionInfo.id);

        const updatedImages = images.map((img) => {
          if (img.id === imageToApply?.id) {
            return { ...img, isMain: true };
          }

          return { ...img, isMain: false };
        });

        dispatch(stateController.setState({ images: updatedImages }));
      } catch (error) {
        notify.error(error.message);
      }
    };
  }

  public static closeModal() {
    return async (dispatch) => {
      dispatch(stateController.setState({ isOpen: false }));
      setTimeout(() => dispatch(stateController.setState(defaultState)), 300);
    };
  }

  public static setSelectedImage(imageId: string) {
    return (dispatch) => {
      dispatch(stateController.setState({ selectedImageId: imageId }));
    };
  }

  public static toggleContainFitMode() {
    return (dispatch) => {
      dispatch(stateController.setState((prev) => ({ ...prev, isContainFitMode: !prev.isContainFitMode })));
    };
  }

  private static async loadProductionImages(productionId: string) {
    const {
      selected,
      variant_photo,
      configuration_photos = [],
      production_photos = [],
    } = await ProductionWorkflowService.getProductionWorkflowImages(productionId);

    const selectedId = selected?.id;

    const existingIds = new Set<string>();

    const productPhotos: ProductionImage[] = configuration_photos.reduce<ProductionImage[]>((acc, photo) => {
      if (!existingIds.has(photo.id)) {
        existingIds.add(photo.id);
        acc.push({
          ...photo,
          type: 'product',
          isMain: photo.id === selectedId,
        });
      }
      return acc;
    }, []);

    if (variant_photo && !existingIds.has(variant_photo.id)) {
      existingIds.add(variant_photo.id);
      productPhotos.push({
        ...variant_photo,
        type: 'product',
        isMain: variant_photo.id === selectedId,
      });
    }

    const productionPhotos: ProductionImage[] = production_photos.map((photo) => ({
      ...photo,
      type: 'production',
      isMain: photo.id === selectedId,
    }));

    const isInConfigurationPhotos = configuration_photos.some(({ id }) => id === selectedId);
    const isSameAsVariantPhoto = variant_photo?.id === selectedId;
    const isInProductionPhotos = production_photos.some(({ id }) => id === selectedId);

    const shouldAddSelected = selected && !isInConfigurationPhotos && !isSameAsVariantPhoto && !isInProductionPhotos;

    if (shouldAddSelected) {
      productionPhotos.push({
        ...selected,
        type: 'production',
        isMain: true,
      });
    }

    return [...productionPhotos, ...productPhotos];
  }
}

export const reducer = stateController.getReducer();
